<template>
  <section id="newEvents">
    <v-sheet class="d-flex flex-column" style="height: 90vh">
      <v-row no-gutters class="event-creation-title">
        <h3 class="ml-5 my-3"> {{ editing ? 'Edit' : 'Create' }} Event</h3>
      </v-row>
      <v-form class="overflow-x-hidden overflow-y-auto flex-grow-1">
        <v-container style="width: 90%">
          <v-row style="max-width: 700px">
            <v-text-field
              v-model="event.title"
              type="text"
              label="Title"
              class="title-input"
              id="title-input"
              :counter="50"
              required
            ></v-text-field>
          </v-row>
          <v-row>
            <v-col>
              <v-row>
                <time-card
                  :startDate.sync="dateTime.startDate"
                  :startTime.sync="dateTime.startTime"
                  :endDate.sync="dateTime.endDate"
                  :endTime.sync="dateTime.endTime"
                ></time-card>
              </v-row>
              <v-row>
                <location-card
                  :zip.sync="event.location.zip"
                  :street.sync="event.location.street"
                  :name.sync="event.location.name"
                  :state.sync="event.location.state"
                  :city.sync="event.location.city"
                ></location-card>
              </v-row>
              <v-row class="mt-2">
                <info-card
                  :hosts.sync="event.hosts"
                  :dressCode.sync="event.dressCode"
                >
                </info-card>
              </v-row>
            </v-col>
            <v-col style="max-height: 580px">
              <image-card
                :src.sync="event.image"
              ></image-card>
            </v-col>
          </v-row>
          <v-row>
            <description-card
              :shortDescription.sync="event.shortDescription"
              :fullDescription.sync="event.fullDescription"
            ></description-card>
          </v-row>
        </v-container>
      </v-form>
      <div class="py-4 d-flex flex-column border-top">
        <p v-if="!!errorMessage" class="error--text mx-3 text-center">{{ errorMessage }}</p>
        <div class="d-flex">
          <v-btn @click="close" class="ml-4" tile color="accent">Close</v-btn>
          <div class="flex-fill"></div>
          <v-btn @click="handleClone" class="mr-4" tile color="primary" v-if="editing">Clone Event</v-btn>
          <v-btn @click="create" class="mr-4" tile color="primary"> {{ editing ? 'Update' : 'Create' }}</v-btn>
        </div>
      </div>
    </v-sheet>
    <v-snackbar
      v-model="snackbar"
      :timeout="10000"
      color="error"
    >
      {{ snackbarText }}
      <v-btn
        text
        @click="snackbarText = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </section>
</template>

<script>
import {mapActions} from 'vuex'
import TimeCard from './EventsTimeCard'
import LocationCard from './EventsLocationCard'
import DescriptionCard from './EventsDescriptionCard'
import ImageCard from './EventsImageCard'
import InfoCard from './EventsInfoCard'
import {maxLength, minLength, required} from 'vuelidate/lib/validators'
import isAfter from '../../../../core/utils/validators/isAfter'
import isBefore from 'date-fns/isBefore'
import {eventImageRef, eventsCollection} from '@/core/config/firebase.js'

const resizeImage = require('resize-image');
const uuidv1 = require('uuid/v1');
import { addHours, format, parse as dateFnsParse, setHours } from "date-fns"
import { cloneDeep } from 'lodash'
import {removeTokenFromUrl} from "@/core/utils";

const dateParseString = "yyyy-MM-dd HH:mm";


export default {
  name: 'createModifyEvents',
  components: {
    TimeCard,
    LocationCard,
    DescriptionCard,
    ImageCard,
    InfoCard
  },
  metaInfo: {
    title: 'Landing',
  },
  watch: {
    "dateTime.startDate"() {
      this.event.startDate = dateFnsParse(`${this.dateTime.startDate} ${this.dateTime.startTime}`, dateParseString, new Date())
      if(this.event.startDate.getTime() > this.event.endDate.getTime()) {
        this.event.endDate = addHours(this.event.startDate, 1)
        this.dateTime.endDate = format(this.event.endDate, "yyyy-MM-dd")
      }
    },
    "dateTime.startTime"() {
      this.event.startDate = dateFnsParse(`${this.dateTime.startDate} ${this.dateTime.startTime}`, dateParseString, new Date())
      if(this.event.startDate.getTime() > this.event.endDate.getTime()) {
        this.event.endDate = addHours(this.event.startDate, 1)
        this.dateTime.endTime = format(this.event.endDate, "HH:mm")
      }
    },
    "dateTime.endDate"() {
      this.event.endDate = dateFnsParse(`${this.dateTime.endDate} ${this.dateTime.endTime}`, dateParseString, new Date())
    },
    "dateTime.endTime"() {
      this.event.endDate = dateFnsParse(`${this.dateTime.endDate} ${this.dateTime.endTime}`, dateParseString, new Date())
    },
    value() {
      this.loadEvent();
    }
  },
  props: {
    value: {
      type: Object,
      required: false
    }
  },
  data() {
    const today = new Date();
    return {
      dateTime: {
        startDate: format(today, "yyyy-MM-dd"),
        startTime: '12:00',
        endDate: format(today, "yyyy-MM-dd"),
        endTime: '13:00',
      },
      errorMessage: '',
      errors: false,
      documentID: undefined,
      snackbar: false,
      snackbarText: "There was an error creating the event.",
      editing: false,
      event: {
        title: '',
        dressCode: '',
        startDate: setHours(today, 12),
        endDate: setHours(today, 13),
        location: {
          name: '',
          state: 'VA',
          city: '',
          street: '',
          zip: ''
        },
        shortDescription: '',
        fullDescription: '',
        published: true,
        hidden: false,
        hosts: '',
        editors: [],
        image: '',
        eventCreated: undefined,
        lastUpdated: undefined
      },
    }
  },
  validations: {
    event: {
      title: {
        required,
        minLength: minLength(5),
        maxLength: maxLength(50)
      },
      startDate: {
        required
      },
      endDate: {
        required,
        afterStart: isAfter('startDate', isBefore)
      },
      shortDescription: {
        required,
        maxLength: maxLength(128)
      },
      fullDescription: {
        required
      }
    }
  },
  methods: {
    ...mapActions('events', ['pullEvent']),
    async close() {
      this.$emit('close');
    },
    handleClone() {
      // Update event object to remove specific information
      const event = cloneDeep(this.event)
      delete event.rsvp
      event.eventCreated = undefined;
      event.clonedFrom = this.documentID
      this.event = event

      // Switch mode to 'create'
      this.editing = false;
      this.documentID = null;
    },
    resetForm() {
      const today = new Date();
      this.dateTime =  {
        startDate: format(today, "yyyy-MM-dd"),
        startTime: '12:00',
        endDate: format(today, "yyyy-MM-dd"),
        endTime: '13:00',
      };
      this.errorMessage = '';
      this.errors = false;
      this.documentID = null;
      this.snackbar = false;
      this.snackbarText = "There was an error creating the event.";
      this.editing = false;
      this.event = {
        title: '',
        dressCode: '',
        startDate: setHours(today, 12),
        endDate: setHours(today, 13),
        location: {
          name: '',
          state: 'VA',
          city: '',
          street: '',
          zip: ''
        },
        shortDescription: '',
        fullDescription: '',
        published: true,
        hidden: false,
        hosts: '',
        editors: [],
        image: '',
        eventCreated: undefined,
        lastUpdated: undefined
      }
    },
    validate() {
      this.errorMessage = '';
      this.errors = false;

      this.$v.event.$touch();

      if (!this.$v.event.$error)
        return true;

      if (this.$v.event.title.$error)
        this.errorMessage += 'A title must be between 5-50 characters long. '

      if (this.$v.event.startDate.$error)
        this.errorMessage += 'Start data is required. '

      if (this.$v.event.endDate.$error)
        this.errorMessage += 'End data is required and must be after the start date. '

      if (this.$v.event.shortDescription.$error)
        this.errorMessage += 'The short description is required and must be under 128 characters long. '

      if (this.$v.event.fullDescription.$error)
        this.errorMessage += 'A full description is required. '

      return false;
    },
    async create() {
      if(!this.validate())
          return;

      let doc, docID;

      if (!!this.documentID) {
        docID = this.documentID;
      } else {
        docID = uuidv1();

        this.documentID = docID}

      doc = eventsCollection.doc(docID);
      this.event.lastUpdated = new Date();

      if (!this.event.eventCreated)
        this.event.eventCreated = this.event.lastUpdated;

      const event = cloneDeep(this.event);

      if (event.image && event.image.startsWith('data:')) {
        let imageRef = eventImageRef.child(docID + '.jpeg');
        const data = await this.addImageProcess(event.image);
        const file = this.dataURItoBlob(data);
        const uploadedFile = await imageRef.put(file);
        const url = await eventImageRef.child(docID + '.jpeg').getDownloadURL();
        event.image = removeTokenFromUrl(url);
      }

      try {
        await doc.set(event);
        const eventObject = await this.pullEvent(docID);
        this.$emit('eventCreated');
        this.resetForm();
      } catch (error) {
        this.snackbar = true;
        console.error(error);
      }
    },
    addImageProcess(src) {
      return new Promise((resolve, reject) => {
        let img = new Image();
        img.onload = () => {
          const data = resizeImage.resize(img, 400, 500, resizeImage.JPEG);
          resolve(data)
        };
        img.onerror = reject;
        img.src = src
      })
    },
    dataURItoBlob(dataURI) {
      // from https://stackoverflow.com/questions/19032406/convert-html5-canvas-into-file-to-be-uploaded
      // convert base64/URLEncoded data component to raw binary data held in a string
      let byteString;
      if (dataURI.split(',')[0].indexOf('base64') >= 0)
        byteString = atob(dataURI.split(',')[1]);
      else
        byteString = unescape(dataURI.split(',')[1]);
      // separate out the mime component
      let mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
      // write the bytes of the string to a typed array
      let ia = new Uint8Array(byteString.length);
      for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ia], {type: mimeString});
    },
    loadEvent() {
      if (!this.value) {
        this.resetForm();
      } else {

        this.documentID = this.value.id;
        const event = JSON.parse(JSON.stringify(this.value))

        if (typeof event.startDate === 'string')
          event.startDate = new Date(event.startDate);

        if (typeof event.endDate === 'string')
          event.endDate = new Date(event.endDate);

        if (typeof event.endDate === 'string')
          event.endDate = new Date(event.endDate);

        if (typeof event.eventCreated === 'string')
          event.eventCreated = new Date(event.eventCreated);

        if (typeof event.lastUpdated === 'string')
          event.lastUpdated = new Date(event.lastUpdated);

        delete event.id;

        this.dateTime.startDate = format(event.startDate, "yyyy-MM-dd")
        this.dateTime.endDate = format(event.endDate, "yyyy-MM-dd")
        let startMin = event.startDate.getMinutes();
        if (startMin < 10) {
          startMin = "0" + startMin;
        }
        this.dateTime.startTime = format(event.startDate, "HH:mm")
        this.dateTime.endTime = format(event.endDate, "HH:mm")
        this.event = event;
        this.editing = true;
      }
    }
  },
  mounted() {
    this.loadEvent();
  },
}
</script>
<style>
#full-description-editor {
  width: 100%
}

.width-100 {
  width: 100% !important;
}

.event-creation-title {
  border-bottom: solid 1px #c7c7c7;
  z-index: 20;
}

.event-creation-footer {
  border-top: solid 1px #c7c7c7;
  z-index: 20;
}

#title-input {
  min-height: 40px !important;
  font-size: 20pt;
}

.title-input label[for] {
  height: 40px;
  font-size: 14pt;
}
</style>
